import { useRef, useEffect, useState, useCallback } from 'react';
import { KEYBOARD_SHORTCUTS, setFPS as setGlobalFPS } from '../utils/constants';
import { getFrameForTime, getTimeForFrame } from '../utils/frameIndex';
import { WebCodecsPlayer } from '../utils/webCodecsPlayer';
import { setFrameData, clearFrameIndex } from '../utils/frameIndex';

export const useVideoPlayer = (videoUrl, videoFile = null) => {
  const canvasRef = useRef(null);   // <canvas> for WebCodecs display
  const playerRef = useRef(null);
  const wcPlayerRef = useRef(null); // WebCodecsPlayer instance
  const lastKeyPressRef = useRef(0);
  const lastButtonClickRef = useRef(0);
  const lastUiUpdateRef = useRef(0);
  const [isPaused, setIsPaused] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);  // 30fps — timeline, sidebar, controls
  const [overlayTime, setOverlayTime] = useState(0);  // 60fps — annotation overlay
  const [videoDuration, setVideoDuration] = useState(0);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [playbackRate, setPlaybackRateState] = useState(1);

  // ─── Initialise / re-initialise WebCodecsPlayer when videoUrl changes ──

  useEffect(() => {
    if (!videoUrl) return;

    const canvasEl = canvasRef.current;
    if (!canvasEl) return;

    let cancelled = false;

    // Destroy previous instance
    if (wcPlayerRef.current) {
      wcPlayerRef.current.destroy();
      wcPlayerRef.current = null;
    }
    clearFrameIndex();
    setIsPlayerReady(false);

    const player = new WebCodecsPlayer();
    wcPlayerRef.current = player;

    // Two-tier time updates:
    //   overlayTime — every frame (60fps), drives the annotation overlay
    //   currentTime — throttled to ~30fps, drives timeline/sidebar/controls
    // Canvas drawImage runs at full rate inside WebCodecs (no React).
    // Timeline & VideoControls are wrapped in React.memo and only see
    // currentTime, so they skip the 60fps overlay-only re-renders.
    const UI_INTERVAL_MS = 33; // ~30 fps
    player.onTimeUpdate = (time) => {
      if (cancelled) return;
      setOverlayTime(time);
      const now = performance.now();
      if (now - lastUiUpdateRef.current >= UI_INTERVAL_MS || player.isPaused) {
        lastUiUpdateRef.current = now;
        setCurrentTime(time);
      }
    };
    player.onPlay = () => { if (!cancelled) setIsPaused(false); };
    player.onPause = () => {
      if (cancelled) return;
      setIsPaused(true);
      const t = player.currentTime;
      setCurrentTime(t);
      setOverlayTime(t);
    };
    player.onEnded = () => {
      if (cancelled) return;
      setIsPaused(true);
      const t = player.currentTime;
      setCurrentTime(t);
      setOverlayTime(t);
    };

    // Build the playerRef API (same interface the rest of the app uses)
      playerRef.current = {
        currentTime: (time) => {
          if (time !== undefined) {
          player.seekToTime(time); // fire-and-forget async
          }
        return player.currentTime;
        },
      duration: () => player.duration,
      paused: () => player.isPaused,
      play: () => player.play(),
      pause: () => player.pause(),
      src: () => {},       // no-op — source handled by init
      load: () => {},      // no-op
      readyState: () => (player.isReady ? 4 : 0),
        dispose: () => {
        player.destroy();
      },
      // Expose the canvas as videoElement for drawImage compatibility.
      // We also attach videoWidth / videoHeight so existing code works.
      get videoElement() { return canvasEl; },
      get videoWidth() { return player.width; },
      get videoHeight() { return player.height; },
    };

    // Init WebCodecs player — pass the File object directly when available
    // to avoid fetch() on blob: URLs (blocked by CSP in production).
    const source = videoFile || videoUrl;
    player.init(source, canvasEl).then(() => {
      if (cancelled) return;

      // Populate the shared frame-index module and update global FPS
      setFrameData(player.frameTimestamps, player.fps);
      setGlobalFPS(player.fps);

      setVideoDuration(player.duration);
      setIsPlayerReady(true);

      console.log('WebCodecs player initialised — frame-accurate seeking enabled');
    }).catch((err) => {
      console.error('WebCodecs player init failed:', err);
    });

    return () => {
      cancelled = true;
      player.destroy();
      clearFrameIndex();
      wcPlayerRef.current = null;
    };
  }, [videoUrl]);

  // ─── Play / Pause handler ──────────────────────────────────────────

  const setPlaybackRate = useCallback((rate) => {
    const player = wcPlayerRef.current;
    if (player) player.setPlaybackRate(rate);
    setPlaybackRateState(rate);
  }, []);

  const handlePlayPauseClick = useCallback((event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const now = Date.now();
    if (now - lastButtonClickRef.current < 50) return;
    lastButtonClickRef.current = now;
    
    const player = wcPlayerRef.current;
    if (!player || !player.isReady) return;

    if (player.isPaused) {
      player.play();
    } else {
      player.pause();
    }
  }, []);

  // ─── Keyboard shortcuts ────────────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (event) => {
      const tag = event.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      const now = Date.now();
      if (now - lastKeyPressRef.current < 50) return;
      lastKeyPressRef.current = now;
      
      const player = wcPlayerRef.current;
      if (!player || !player.isReady) return;

      const skipFrames = event.shiftKey ? 50 : 1;
      
      switch (event.key) {
        case KEYBOARD_SHORTCUTS.FRAME_BACK:
          event.preventDefault();
          event.stopPropagation();
          player.stepBackward(skipFrames);
          break;
        case KEYBOARD_SHORTCUTS.FRAME_FORWARD:
          event.preventDefault();
          event.stopPropagation();
          player.stepForward(skipFrames);
          break;
        case KEYBOARD_SHORTCUTS.PLAY_PAUSE:
          event.preventDefault();
          event.stopPropagation();
          handlePlayPauseClick(event);
          break;
        default:
          break;
      }
    };
    
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [handlePlayPauseClick]);

  return {
    canvasRef,
    playerRef,
    isPaused,
    currentTime,
    overlayTime,
    videoDuration,
    handlePlayPauseClick,
    isPlayerReady,
    playbackRate,
    setPlaybackRate,
  };
}; 
